/**
 * Partner Project Modal
 * Add/Edit partner GCP projects
 */

import React, { useState, useEffect } from 'react';
import { collection, addDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuthStore } from '../store/authStore';
import './PartnerProjectModal.css';

const PartnerProjectModal = ({ project, clients, onClose }) => {
  const { user } = useAuthStore();
  const isEditing = !!project;

  const [formData, setFormData] = useState({
    gcpProjectId: '',
    gcpProjectName: '',
    clientId: '',
    billingAccountId: '017FA2-051A31-49FBEC',
    monthlyBudget: 500,
    alertThresholds: [50, 80, 100],
    billingEnabled: true,
    autoChargeEnabled: false,
    marginType: 'fixed',
    percentageMarkup: 0,
    fixedFee: 100,
    fixedFeeDescription: 'Operations & Monitoring',
    estimatedCosts: [
      { service: 'Firebase Hosting', rawCost: 50 },
      { service: 'Cloud Functions', rawCost: 30 },
      { service: 'Firestore', rawCost: 30 },
      { service: 'Cloud Storage', rawCost: 20 },
    ],
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (project) {
      setFormData({
        gcpProjectId: project.gcpProjectId || '',
        gcpProjectName: project.gcpProjectName || '',
        clientId: project.clientId || '',
        billingAccountId: project.billingAccountId || '017FA2-051A31-49FBEC',
        monthlyBudget: project.monthlyBudget || 500,
        alertThresholds: project.alertThresholds || [50, 80, 100],
        billingEnabled: project.billingEnabled ?? true,
        autoChargeEnabled: project.autoChargeEnabled ?? false,
        marginType: project.marginConfig?.type || 'fixed',
        percentageMarkup: (project.marginConfig?.percentageMarkup || 0) * 100,
        fixedFee: project.marginConfig?.fixedFee || 100,
        fixedFeeDescription: project.marginConfig?.fixedFeeDescription || 'Operations & Monitoring',
        estimatedCosts: project.estimatedCosts || [
          { service: 'Firebase Hosting', rawCost: 50 },
          { service: 'Cloud Functions', rawCost: 30 },
          { service: 'Firestore', rawCost: 30 },
          { service: 'Cloud Storage', rawCost: 20 },
        ],
      });
    }
  }, [project]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleCostChange = (index, field, value) => {
    setFormData((prev) => {
      const newCosts = [...prev.estimatedCosts];
      newCosts[index] = { ...newCosts[index], [field]: field === 'rawCost' ? parseFloat(value) || 0 : value };
      return { ...prev, estimatedCosts: newCosts };
    });
  };

  const addCostLine = () => {
    setFormData((prev) => ({
      ...prev,
      estimatedCosts: [...prev.estimatedCosts, { service: '', rawCost: 0 }],
    }));
  };

  const removeCostLine = (index) => {
    setFormData((prev) => ({
      ...prev,
      estimatedCosts: prev.estimatedCosts.filter((_, i) => i !== index),
    }));
  };

  const calculateTotal = () => {
    const rawTotal = formData.estimatedCosts.reduce((sum, c) => sum + (c.rawCost || 0), 0);
    let bufferedTotal = rawTotal;

    if (formData.marginType === 'percentage' || formData.marginType === 'both') {
      bufferedTotal = rawTotal * (1 + (formData.percentageMarkup || 0) / 100);
    }
    if (formData.marginType === 'fixed' || formData.marginType === 'both') {
      bufferedTotal += formData.fixedFee || 0;
    }

    return { rawTotal, bufferedTotal, margin: bufferedTotal - rawTotal };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const projectData = {
        gcpProjectId: formData.gcpProjectId.trim(),
        gcpProjectName: formData.gcpProjectName.trim(),
        clientId: formData.clientId,
        clientName: clients.find((c) => c.id === formData.clientId)?.name || '',
        billingAccountId: formData.billingAccountId.trim(),
        bigqueryDataset: 'fusionict-billing.billing_export',
        monthlyBudget: parseFloat(formData.monthlyBudget) || 500,
        alertThresholds: formData.alertThresholds,
        billingEnabled: formData.billingEnabled,
        billingCurrency: 'USD',
        billingCycleDay: 1,
        autoChargeEnabled: formData.autoChargeEnabled,
        tier: 'launch',
        status: 'active',
        marginConfig: {
          type: formData.marginType,
          percentageMarkup: formData.marginType === 'fixed' ? 0 : (parseFloat(formData.percentageMarkup) || 0) / 100,
          fixedFee: formData.marginType === 'percentage' ? 0 : parseFloat(formData.fixedFee) || 0,
          fixedFeeDescription: formData.fixedFeeDescription,
        },
        estimatedCosts: formData.estimatedCosts.filter((c) => c.service && c.rawCost > 0),
        updatedAt: serverTimestamp(),
      };

      if (isEditing) {
        await updateDoc(doc(db, 'gcpProjects', project.id), projectData);
      } else {
        projectData.userId = user.uid;
        projectData.createdAt = serverTimestamp();
        await addDoc(collection(db, 'gcpProjects'), projectData);
      }

      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const totals = calculateTotal();

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content project-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditing ? 'Edit Partner Project' : 'Add Partner Project'}</h2>
          <button className="modal-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="error-message">{error}</div>}

            <div className="form-section">
              <h3>Project Information</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>GCP Project ID *</label>
                  <input
                    type="text"
                    name="gcpProjectId"
                    value={formData.gcpProjectId}
                    onChange={handleChange}
                    placeholder="e.g., my-project-123"
                    required
                    disabled={isEditing}
                  />
                  <small>The actual Google Cloud project identifier</small>
                </div>

                <div className="form-group">
                  <label>Display Name *</label>
                  <input
                    type="text"
                    name="gcpProjectName"
                    value={formData.gcpProjectName}
                    onChange={handleChange}
                    placeholder="e.g., Client App Production"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Client *</label>
                  <select
                    name="clientId"
                    value={formData.clientId}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select a client...</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Billing Account ID</label>
                  <input
                    type="text"
                    name="billingAccountId"
                    value={formData.billingAccountId}
                    onChange={handleChange}
                    placeholder="XXXXXX-XXXXXX-XXXXXX"
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Budget & Alerts</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Monthly Budget (USD)</label>
                  <input
                    type="number"
                    name="monthlyBudget"
                    value={formData.monthlyBudget}
                    onChange={handleChange}
                    min="0"
                    step="50"
                  />
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="billingEnabled"
                      checked={formData.billingEnabled}
                      onChange={handleChange}
                    />
                    Billing Enabled
                  </label>
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="autoChargeEnabled"
                      checked={formData.autoChargeEnabled}
                      onChange={handleChange}
                    />
                    Auto-Charge Enabled
                  </label>
                  <small>Automatically charge client's card monthly</small>
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Margin Configuration</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Margin Type</label>
                  <select
                    name="marginType"
                    value={formData.marginType}
                    onChange={handleChange}
                  >
                    <option value="fixed">Fixed Fee</option>
                    <option value="percentage">Percentage Markup</option>
                    <option value="both">Both (Percentage + Fixed)</option>
                  </select>
                </div>

                {(formData.marginType === 'percentage' || formData.marginType === 'both') && (
                  <div className="form-group">
                    <label>Percentage Markup (%)</label>
                    <input
                      type="number"
                      name="percentageMarkup"
                      value={formData.percentageMarkup}
                      onChange={handleChange}
                      min="0"
                      max="100"
                      step="1"
                    />
                  </div>
                )}

                {(formData.marginType === 'fixed' || formData.marginType === 'both') && (
                  <>
                    <div className="form-group">
                      <label>Fixed Fee (USD)</label>
                      <input
                        type="number"
                        name="fixedFee"
                        value={formData.fixedFee}
                        onChange={handleChange}
                        min="0"
                        step="25"
                      />
                    </div>

                    <div className="form-group">
                      <label>Fee Description</label>
                      <input
                        type="text"
                        name="fixedFeeDescription"
                        value={formData.fixedFeeDescription}
                        onChange={handleChange}
                        placeholder="e.g., Operations & Monitoring"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="form-section">
              <div className="section-header">
                <h3>Estimated Monthly Costs</h3>
                <button type="button" className="btn btn-sm btn-secondary" onClick={addCostLine}>
                  + Add Service
                </button>
              </div>

              <table className="costs-table">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Raw Cost (USD)</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {formData.estimatedCosts.map((cost, index) => (
                    <tr key={index}>
                      <td>
                        <input
                          type="text"
                          value={cost.service}
                          onChange={(e) => handleCostChange(index, 'service', e.target.value)}
                          placeholder="Service name"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={cost.rawCost}
                          onChange={(e) => handleCostChange(index, 'rawCost', e.target.value)}
                          min="0"
                          step="5"
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => removeCostLine(index)}
                        >
                          &times;
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="totals-row">
                    <td>Raw Total</td>
                    <td colSpan="2">${totals.rawTotal.toFixed(2)}</td>
                  </tr>
                  <tr className="totals-row highlight">
                    <td>Billed Total</td>
                    <td colSpan="2">${totals.bufferedTotal.toFixed(2)}</td>
                  </tr>
                  <tr className="totals-row margin-row">
                    <td>Your Margin</td>
                    <td colSpan="2">${totals.margin.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : isEditing ? 'Update Project' : 'Add Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PartnerProjectModal;
